import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, UsersIcon, LightbulbIcon, ActivityIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
type Specialty = 'cardiologist' | 'nephrologist' | 'gp' | 'endocrinologist';
interface RewardSlideProps {
  questionIndex: number;
  selectedAnswer: string;
  selectedSpecialty: Specialty;
  visibleSpecialties: string[];
  onContinue: () => void;
}
const COLORS: Record<string, string> = {
  Cardiologist: '#E11D48',
  Nephrologist: '#0D9488',
  GP: '#6366F1',
  Endocrinologist: '#F59E0B'
};
const ALL_SPECIALTY_KEYS = ['Cardiologist', 'Nephrologist', 'GP', 'Endocrinologist'];
const mockData = {
  0: {
    chartData: [{
      name: 'Long-term outcomes',
      Cardiologist: 45,
      Nephrologist: 40,
      GP: 15,
      Endocrinologist: 35
    }, {
      name: 'Simplicity',
      Cardiologist: 10,
      Nephrologist: 15,
      GP: 45,
      Endocrinologist: 20
    }, {
      name: 'Adherence',
      Cardiologist: 15,
      Nephrologist: 20,
      GP: 30,
      Endocrinologist: 25
    }, {
      name: 'Evidence strength',
      Cardiologist: 30,
      Nephrologist: 25,
      GP: 10,
      Endocrinologist: 20
    }],
    gapAnnotation: 'Largest gap: GPs prioritize simplicity over long-term outcomes compared to specialists.',
    tribes: {
      'Long-term cardiovascular outcomes': {
        name: 'Outcome-first clinicians',
        desc: 'You prioritize long-term disease modification over immediate convenience.'
      },
      'Simplicity of treatment regimen': {
        name: 'Pragmatic optimizers',
        desc: 'You focus on real-world practicality and reducing patient burden.'
      },
      'Patient adherence likelihood': {
        name: 'Patient-centric adapters',
        desc: 'You believe the best drug is the one the patient will actually take.'
      },
      'Evidence strength and guidelines': {
        name: 'Guideline-driven practitioners',
        desc: 'You rely on robust clinical data and established protocols.'
      }
    },
    whyModel: {
      Cardiologist: {
        patient: 'High-risk, complex',
        responsibility: 'Long-term survival',
        constraints: 'Specialist access delays'
      },
      Nephrologist: {
        patient: 'Progressing chronic',
        responsibility: 'Delaying progression',
        constraints: 'Care coordination'
      },
      GP: {
        patient: 'Broad, undifferentiated',
        responsibility: 'Holistic management',
        constraints: 'Severe time limits (15m visits)'
      },
      Endocrinologist: {
        patient: 'Metabolic multi-morbidity',
        responsibility: 'Systemic control',
        constraints: 'Polypharmacy management'
      }
    },
    whyExplanation: 'GPs operate under severe time constraints driving simpler decisions, while specialists focus on long-term progression risk due to their specific accountability.'
  },
  1: {
    chartData: [{
      name: 'Lower side effects',
      Cardiologist: 40,
      Nephrologist: 45,
      GP: 25,
      Endocrinologist: 35
    }, {
      name: 'Dosing convenience',
      Cardiologist: 15,
      Nephrologist: 10,
      GP: 40,
      Endocrinologist: 20
    }, {
      name: 'Cost',
      Cardiologist: 25,
      Nephrologist: 30,
      GP: 20,
      Endocrinologist: 25
    }, {
      name: 'Novel mechanism',
      Cardiologist: 20,
      Nephrologist: 15,
      GP: 15,
      Endocrinologist: 20
    }],
    gapAnnotation: 'Largest gap: GPs highly value dosing convenience to combat polypharmacy.',
    tribes: {
      'Lower side effect profile': {
        name: 'Risk-minimizers',
        desc: 'You prioritize patient safety and tolerability above all else.'
      },
      'Dosing convenience': {
        name: 'Adherence champions',
        desc: 'You focus on reducing regimen complexity to ensure compliance.'
      },
      'Cost and reimbursement': {
        name: 'Access advocates',
        desc: 'You navigate systemic barriers to ensure patients can afford care.'
      },
      'Novelty of mechanism': {
        name: 'Innovation seekers',
        desc: 'You look for new pathways to treat difficult conditions.'
      }
    },
    whyModel: {
      Cardiologist: {
        patient: 'Acute & chronic',
        responsibility: 'Preventing events',
        constraints: 'High stakes outcomes'
      },
      Nephrologist: {
        patient: 'Frail, multi-morbid',
        responsibility: 'Preserving function',
        constraints: 'Limited therapeutic window'
      },
      GP: {
        patient: 'Polypharmacy heavy',
        responsibility: 'Overall compliance',
        constraints: 'Patient confusion/burden'
      },
      Endocrinologist: {
        patient: 'Chronic metabolic',
        responsibility: 'Sustained control',
        constraints: 'Behavioral adherence'
      }
    },
    whyExplanation: 'Polypharmacy dictates convenience for GPs, whereas specialists managing acute risks prioritize minimizing dangerous side effects.'
  },
  2: {
    chartData: [{
      name: 'Evidence uncertainty',
      Cardiologist: 50,
      Nephrologist: 45,
      GP: 20,
      Endocrinologist: 40
    }, {
      name: 'Insurance coverage',
      Cardiologist: 25,
      Nephrologist: 30,
      GP: 45,
      Endocrinologist: 35
    }, {
      name: 'Unfamiliarity',
      Cardiologist: 10,
      Nephrologist: 15,
      GP: 20,
      Endocrinologist: 15
    }, {
      name: 'Prefer established',
      Cardiologist: 15,
      Nephrologist: 10,
      GP: 15,
      Endocrinologist: 10
    }],
    gapAnnotation: 'Largest gap: Specialists demand evidence, while GPs are blocked by insurance.',
    tribes: {
      'Uncertainty in long-term evidence': {
        name: 'Evidence optimizers',
        desc: 'You require robust, long-term data before changing practice.'
      },
      'Patient insurance coverage': {
        name: 'System navigators',
        desc: 'You are acutely aware of formulary restrictions and access issues.'
      },
      'Lack of familiarity with mechanism': {
        name: 'Cautious adopters',
        desc: 'You prefer to fully understand a drug before prescribing.'
      },
      'Preference for established options': {
        name: 'Conservative prescribers',
        desc: 'You stick with what has worked reliably in your experience.'
      }
    },
    whyModel: {
      Cardiologist: {
        patient: 'Guideline-driven',
        responsibility: 'Mortality reduction',
        constraints: 'Need for RCT data'
      },
      Nephrologist: {
        patient: 'Vulnerable',
        responsibility: 'Safety first',
        constraints: 'Fear of adverse events'
      },
      GP: {
        patient: 'Cost-sensitive',
        responsibility: 'Affordable care',
        constraints: 'Prior authorization hurdles'
      },
      Endocrinologist: {
        patient: 'Long-term care',
        responsibility: 'Sustainable access',
        constraints: 'Step-therapy requirements'
      }
    },
    whyExplanation: 'Specialists require robust long-term data before adoption. GPs face reimbursement and access barriers that override evidence considerations.'
  }
};
export function RewardSlide({
  questionIndex,
  selectedAnswer,
  selectedSpecialty,
  visibleSpecialties,
  onContinue
}: RewardSlideProps) {
  const [showWhy, setShowWhy] = useState(false);
  const data = mockData[questionIndex as keyof typeof mockData];
  if (!data) return null;
  const specialtyKey = selectedSpecialty === 'gp' ? 'GP' : selectedSpecialty.charAt(0).toUpperCase() + selectedSpecialty.slice(1);
  // Find the short name of the selected answer to match chart data
  const shortAnswerName = data.chartData.find(d => selectedAnswer.toLowerCase().includes(d.name.toLowerCase()) || d.name.toLowerCase().includes(selectedAnswer.toLowerCase().split(' ')[0]))?.name || data.chartData[0].name;
  const userSpecialtyValue = data.chartData.find(d => d.name === shortAnswerName)?.[specialtyKey as keyof typeof COLORS] as number || 0;
  const maxSpecialtyValue = Math.max(...data.chartData.map(d => (d as Record<string, number | string>)[specialtyKey] as number));
  const isMinority = userSpecialtyValue < maxSpecialtyValue;
  const tribe = data.tribes[selectedAnswer as keyof typeof data.tribes] || Object.values(data.tribes)[0];
  const showingMultiple = visibleSpecialties.length > 1;
  const gapAnnotationVisible = showingMultiple;
  // Filter WHY model to visible specialties
  const visibleWhyEntries = Object.entries(data.whyModel).filter(([spec]) => visibleSpecialties.includes(spec));
  return <div className="w-full min-h-screen flex items-start justify-center bg-[#FAFAF8] px-6 py-16 overflow-y-auto" data-id="element-152">
      <div className="max-w-3xl w-full space-y-6 pt-8" data-id="element-153">
        {/* SECTION 1: GAP */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100" data-id="element-154">
          <div className="flex items-center justify-between mb-6" data-id="element-155">
            <div className="flex items-center gap-2" data-id="element-156">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-rose-100 text-rose-600 text-xs font-bold" data-id="element-157">
                🔴
              </span>
              <h3 className="text-lg font-bold text-[#1C1917]" data-id="element-158">
                {showingMultiple ? 'Where specialists differ most' : 'How your specialty answered'}
              </h3>
            </div>
            {!showingMultiple && <span className="text-xs text-neutral-400 font-medium" data-id="element-159">
                Add specialties to compare →
              </span>}
          </div>

          <div className="h-72 w-full" data-id="element-160">
            <ResponsiveContainer width="100%" height="100%" data-id="element-161">
              <BarChart data={data.chartData} margin={{
              top: 20,
              right: 0,
              left: -20,
              bottom: 20
            }} data-id="element-162">
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} data-id="element-163" />
                <XAxis dataKey="name" tick={{
                fill: '#57534E',
                fontSize: 12
              }} axisLine={false} tickLine={false} dy={10} data-id="element-164" />
                <YAxis tick={{
                fill: '#57534E',
                fontSize: 12
              }} axisLine={false} tickLine={false} tickFormatter={val => `${val}%`} data-id="element-165" />
                <Tooltip cursor={{
                fill: '#F3F4F6',
                opacity: 0.4
              }} contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number, name: string) => [`${value}%`, name]} data-id="element-166" />
                {visibleSpecialties.length > 1 && <Legend iconType="circle" wrapperStyle={{
                fontSize: '12px',
                paddingTop: '10px'
              }} data-id="element-167" />}
                {ALL_SPECIALTY_KEYS.filter(key => visibleSpecialties.includes(key)).map(key => <Bar key={key} dataKey={key} fill={COLORS[key]} radius={[4, 4, 0, 0]} animationDuration={800} data-id="element-168" />)}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Gap annotation — only show when comparing */}
          <AnimatePresence data-id="element-169">
            {gapAnnotationVisible && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="mt-4 p-3 bg-neutral-50 rounded-xl border border-neutral-100 flex items-start gap-3" data-id="element-170">
                <ActivityIcon className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" data-id="element-171" />
                <p className="text-sm text-[#57534E] font-medium" data-id="element-172">
                  {data.gapAnnotation}
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Single specialty hint */}
          {!showingMultiple && <div className="mt-4 p-3 bg-teal-50 rounded-xl border border-teal-100 flex items-start gap-3" data-id="element-173">
              <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{
            backgroundColor: COLORS[specialtyKey]
          }} data-id="element-174" />
              <p className="text-sm text-teal-800 font-medium" data-id="element-175">
                Showing {specialtyKey} responses only. Use the buttons in the
                top-right to add other specialties and reveal the gaps.
              </p>
            </div>}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-176">
          {/* SECTION 2: POSITION */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 flex flex-col" data-id="element-177">
            <div className="flex items-center gap-2 mb-4" data-id="element-178">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold" data-id="element-179">
                🔵
              </span>
              <h3 className="text-lg font-bold text-[#1C1917]" data-id="element-180">
                Where you stand
              </h3>
            </div>

            <div className="flex-1 flex flex-col justify-center items-center text-center space-y-3" data-id="element-181">
              <div className={`text-4xl font-extrabold ${isMinority ? 'text-amber-500' : 'text-blue-600'}`} data-id="element-182">
                {userSpecialtyValue}%
              </div>
              <p className="text-sm text-[#57534E] font-medium" data-id="element-183">
                {isMinority ? `You are in the minority. Only ${userSpecialtyValue}% of ${specialtyKey}s chose this.` : `You align with the majority of ${specialtyKey}s.`}
              </p>
              <div className="px-3 py-1.5 bg-neutral-100 rounded-lg text-xs font-medium text-neutral-600 w-full truncate" data-id="element-184">
                Your pick: {shortAnswerName}
              </div>
            </div>
          </motion.div>

          {/* SECTION 3: TRIBE */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 flex flex-col" data-id="element-185">
            <div className="flex items-center gap-2 mb-4" data-id="element-186">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-bold" data-id="element-187">
                🟣
              </span>
              <h3 className="text-lg font-bold text-[#1C1917]" data-id="element-188">
                You think like...
              </h3>
            </div>

            <div className="flex-1 flex flex-col justify-center space-y-4" data-id="element-189">
              <div className="inline-flex items-center self-start px-3 py-1.5 bg-purple-50 text-purple-700 font-bold rounded-lg text-sm" data-id="element-190">
                {tribe.name}
              </div>
              <p className="text-sm text-[#57534E] leading-relaxed" data-id="element-191">
                {tribe.desc}
              </p>
              <div className="flex items-center gap-2 text-xs text-neutral-400 font-medium pt-2 border-t border-neutral-100" data-id="element-192">
                <UsersIcon className="w-4 h-4" data-id="element-193" />
                This tribe cuts across all specialties
              </div>
            </div>
          </motion.div>
        </div>

        {/* SECTION 4: WHY */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden" data-id="element-194">
          <button onClick={() => setShowWhy(!showWhy)} className="w-full p-6 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors" data-id="element-195">
            <div className="flex items-center gap-3" data-id="element-196">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center" data-id="element-197">
                <LightbulbIcon className="w-5 h-5 text-amber-600" data-id="element-198" />
              </div>
              <div className="text-left" data-id="element-199">
                <h3 className="text-lg font-bold text-[#1C1917]" data-id="element-200">
                  Why do these differences exist?
                </h3>
                <p className="text-sm text-[#57534E]" data-id="element-201">
                  Explore the system constraints driving behavior
                </p>
              </div>
            </div>
            {showWhy ? <ChevronUpIcon className="w-5 h-5 text-neutral-400" data-id="element-202" /> : <ChevronDownIcon className="w-5 h-5 text-neutral-400" data-id="element-203" />}
          </button>

          <AnimatePresence data-id="element-204">
            {showWhy && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-neutral-100" data-id="element-205">
                <div className="p-6 space-y-6" data-id="element-206">
                  <p className="text-sm font-medium text-[#1C1917] bg-amber-50 p-4 rounded-xl border border-amber-100" data-id="element-207">
                    {data.whyExplanation}
                  </p>

                  <div className="overflow-x-auto" data-id="element-208">
                    <table className="w-full text-sm text-left" data-id="element-209">
                      <thead className="text-xs text-neutral-500 uppercase bg-neutral-50" data-id="element-210">
                        <tr data-id="element-211">
                          <th className="px-4 py-3 rounded-tl-lg" data-id="element-212">Specialty</th>
                          <th className="px-4 py-3" data-id="element-213">Patient Type</th>
                          <th className="px-4 py-3" data-id="element-214">Clinical Responsibility</th>
                          <th className="px-4 py-3 rounded-tr-lg" data-id="element-215">
                            System Constraints
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-216">
                        {visibleWhyEntries.map(([spec, model], idx) => <tr key={spec} className={idx !== visibleWhyEntries.length - 1 ? 'border-b border-neutral-100' : ''} data-id="element-217">
                            <td className="px-4 py-3 font-bold text-[#1C1917]" data-id="element-218">
                              <div className="flex items-center gap-2" data-id="element-219">
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                            backgroundColor: COLORS[spec]
                          }} data-id="element-220" />
                                {spec}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-[#57534E]" data-id="element-221">
                              {model.patient}
                            </td>
                            <td className="px-4 py-3 text-[#57534E]" data-id="element-222">
                              {model.responsibility}
                            </td>
                            <td className="px-4 py-3 text-[#57534E]" data-id="element-223">
                              {model.constraints}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {visibleSpecialties.length < 4 && <p className="text-xs text-neutral-400 text-center" data-id="element-224">
                      Add more specialties using the top-right buttons to see
                      the full comparison
                    </p>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* Continue */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="flex justify-center pt-4 pb-20" data-id="element-225">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-226">
            Continue
            <ArrowRightIcon className="w-5 h-5" data-id="element-227" />
          </button>
        </motion.div>
      </div>
    </div>;
}